import { useRef, useState } from 'react';

import {
  type EcommerceCompany,
  EcommerceCompanyLookupError,
  useEcommerceCompanyMutation,
} from '@/api/ecommerce';
import Button, { ButtonLoading } from '@/components/ui/Button';
import { validatorNip } from '@/helpers/nipValidator';

import { normalizeTaxId } from '../helpers/companyInvoice';
import type { EcommerceFormValues } from '../schema';
import { COMPANY_NAME_NAME } from './InputCompanyName';
import InputTaxId, { TAX_ID_NAME } from './InputTaxId';
import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

export type ManualEntryReason = 'not_found' | 'unavailable';

interface Props {
  autoFocus?: boolean;
  error?: string;
  onFound: (company: EcommerceCompany) => void;
  onManualEntry: (reason: ManualEntryReason) => void;
}

const CompanyLookupField = ({
  autoFocus,
  error,
  onFound,
  onManualEntry,
}: Props) => {
  const { i18n } = useLingui();
  const { clearErrors, trigger, watch } = useFormContext<EcommerceFormValues>();
  const companyLookup = useEcommerceCompanyMutation();
  const isLookupInFlight = useRef(false);
  const [notFoundTaxId, setNotFoundTaxId] = useState<string | null>(null);

  const taxId = normalizeTaxId(watch(TAX_ID_NAME) ?? '');
  const isTaxIdValid = validatorNip(taxId)();
  const isNotFound = !!taxId && notFoundTaxId === taxId;

  const handleLookup = () => {
    if (isLookupInFlight.current) return;
    // Przycisk jest aktywny także przy pustym / błędnym NIP-ie (jak w Figmie),
    // więc zamiast zapytania pokazujemy błąd walidacji pod polem.
    if (!isTaxIdValid) {
      trigger(TAX_ID_NAME, { shouldFocus: true });
      return;
    }
    isLookupInFlight.current = true;
    setNotFoundTaxId(null);
    clearErrors([TAX_ID_NAME, COMPANY_NAME_NAME]);

    companyLookup.mutate(taxId, {
      onSuccess: onFound,
      onError: (lookupError) => {
        if (
          lookupError instanceof EcommerceCompanyLookupError &&
          lookupError.isNotFound
        ) {
          setNotFoundTaxId(taxId);
          return;
        }
        onManualEntry('unavailable');
      },
      onSettled: () => {
        isLookupInFlight.current = false;
      },
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="caption-large text-dark-gray-500">
        {i18n.t(
          'Wpisz NIP firmy — nazwę i adres pobierzemy automatycznie z bazy GUS.'
        )}
      </p>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex-1">
          <InputTaxId
            autoFocus={autoFocus}
            disabled={companyLookup.isPending}
            lookupError={
              isNotFound
                ? i18n.t(
                    'Nie znaleźliśmy firmy o tym numerze NIP. Sprawdź, czy numer jest poprawny.'
                  )
                : undefined
            }
            companyDataError={error}
          />
        </div>
        <Button
          type="button"
          size="small"
          className="h-[52px] shrink-0 sm:w-fit"
          loading={companyLookup.isPending}
          aria-busy={companyLookup.isPending}
          onClick={handleLookup}
        >
          {companyLookup.isPending
            ? i18n.t('Pobieranie danych')
            : i18n.t('Pobierz dane')}
          <ButtonLoading className="text-white" />
        </Button>
      </div>

      {isNotFound && (
        <button
          type="button"
          className="caption-large w-fit text-blue-600 underline underline-offset-2 hover:text-blue-700"
          onClick={() => onManualEntry('not_found')}
        >
          {i18n.t('Uzupełnij dane ręcznie')}
        </button>
      )}
    </div>
  );
};

export default CompanyLookupField;
